/**
 * API service with Axios interceptors for handling authentication
 */
import axios from 'axios';
// Import auth functions from the auth service
import authService from './auth';
import { API_BASE_URL } from '../utils/constants';

const API_URL = API_BASE_URL;

// Debug helper: only logs when localStorage.DEBUG_API === '1'
const debug = (...args) => {
  try {
    if (typeof window !== 'undefined' && window.localStorage && localStorage.getItem('DEBUG_API') === '1') {
      // eslint-disable-next-line no-console
      console.log(...args);
    }
  } catch (_) { /* ignore */ }
};

/**
 * Create an Axios instance with base configuration
 */
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 60000 // 60 seconds default timeout to support long-running operations
});

/**
 * Normalize any absolute URL that points to an app* host to its api* host.
 * Examples:
 *  https://appd.thaklis.com -> https://apid.thaklis.com
 *  https://app.thaklis.com  -> https://api.thaklis.com
 */
const normalizeApiHost = (rawUrl) => {
  try {
    if (typeof rawUrl !== 'string' || rawUrl.length === 0) return rawUrl;
    const u = new URL(rawUrl);
    const parts = u.hostname.split('.');
    if (parts.length > 0 && /^app/i.test(parts[0])) {
      parts[0] = parts[0].replace(/^app/i, 'api');
      u.hostname = parts.join('.');
      return u.toString();
    }
    return rawUrl;
  } catch (_) {
    return rawUrl; // non-absolute URL or parse error; ignore
  }
};

/**
 * Ensure config has a proper baseURL for relative paths like /v1/*.
 * If baseURL is missing, set it to our normalized API_URL.
 */
const ensureBaseForRelative = (config) => {
  try {
    const url = config.url || '';
    const isRelativeApi = typeof url === 'string' && url.startsWith('/') && (url.startsWith('/v1') || url.startsWith('/api'));
    if (isRelativeApi && !config.baseURL) {
      config.baseURL = API_URL;
    }
  } catch (_) { /* ignore */ }
  return config;
};

/**
 * Request interceptor to add authentication token to all requests
 */
api.interceptors.request.use(
  config => {
    // Ensure relative API paths use normalized baseURL
    ensureBaseForRelative(config);

    // Normalize absolute URLs accidentally pointing to app* hosts
    if (config.url && /^https?:\/\//i.test(config.url)) {
      config.url = normalizeApiHost(config.url);
    }

    // Add token to request headers if available
    const token = authService.getToken();
    
    // Log detailed token information for debugging
    debug(`\n=== API REQUEST TO: ${config.url} ===`);
    debug(`Token available: ${!!token}`);
    
    if (token) {
      // Check if token is in proper format
      const tokenParts = token.split('.');
      const isValidFormat = tokenParts.length === 3;
      debug(`Token format valid: ${isValidFormat}`);
      
      if (isValidFormat) {
        try {
          // Try to decode the payload
          const payload = JSON.parse(atob(tokenParts[1]));
          debug('Token payload:', payload);
          
          // Check expiration
          const now = Math.floor(Date.now() / 1000);
          const isExpired = payload.exp && payload.exp < now;
          debug(`Token expired: ${isExpired}`);
          debug(`Expiration time: ${new Date(payload.exp * 1000).toLocaleString()}`);
          debug(`Current time: ${new Date().toLocaleString()}`);
          
          // Check user info
          if (payload.sub) {
            debug(`User ID: ${payload.sub}`);
          }
        } catch (e) {
          console.error('Error decoding token:', e);
        }
      }
      
      // Add token to headers
      config.headers['Authorization'] = `Bearer ${token}`;
      debug('Authorization header added to request');
    } else {
      debug('No token available for request');
    }
    
    debug('Request URL:', config.url);
    debug('Request method:', config.method);
    debug('Request headers:', config.headers);
    debug('=====================================\n');
    
    return config;
  },
  error => {
    console.error('Request error:', error);
    return Promise.reject(error);
  }
);

/**
 * Response interceptor to handle token refresh on 401 errors
 */
api.interceptors.response.use(
  // For successful responses, just return the response
  response => response,
  
  // For error responses, handle token refresh if needed
  async error => {
    const originalRequest = error.config;
    
    // If the error is not 401 or the request has already been retried, reject
    if (!error.response || error.response.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }
    
    // Mark the request as retried to prevent infinite loops
    originalRequest._retry = true;
    
    try {
      // Attempt to refresh the token
      const newToken = await authService.refreshToken();
      
      if (newToken) {
        // Update the request with the new token
        originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
        
        // Retry the original request with the new token
        return api(originalRequest);
      } else {
        // If token refresh failed, clear tokens and reject
        authService.clearTokens();
        
        // Optionally redirect to login page
        window.location.href = '/login';
        
        return Promise.reject(error);
      }
    } catch (refreshError) {
      console.error('Token refresh failed:', refreshError);
      authService.clearTokens();
      
      // Optionally redirect to login page
      window.location.href = '/login';
      
      return Promise.reject(error);
    }
  }
);

/**
 * Generic API methods
 */

/**
 * Perform a GET request
 * @param {string} url - API endpoint
 * @param {Object} params - Query parameters
 * @returns {Promise<any>} Response data
 */
export const get = async (url, params = {}) => {
  try {
    const response = await api.get(url, { params });
    return response.data;
  } catch (error) {
    console.error(`GET ${url} failed:`, error);
    throw error;
  }
};

/**
 * Perform a POST request
 * @param {string} url - API endpoint
 * @param {Object} data - Request body
 * @returns {Promise<any>} Response data
 */
export const post = async (url, data = {}) => {
  try {
    const response = await api.post(url, data);
    return response.data;
  } catch (error) {
    console.error(`POST ${url} failed:`, error);
    
    // Log detailed error information
    if (error.response) {
      debug('Error response status:', error.response.status);
      debug('Error response headers:', error.response.headers);
      debug('Error data:', error.response.data);
      
      // If the response data is a string (which is the case with our ResponseStatusException handling),
      // ensure it's accessible in the normal error path
      if (typeof error.response.data === 'string') {
        error.message = error.response.data;
      }
    }
    
    throw error;
  }
};

/**
 * Perform a PUT request
 * @param {string} url - API endpoint
 * @param {Object} data - Request body
 * @returns {Promise<any>} Response data
 */
export const put = async (url, data = {}) => {
  try {
    const response = await api.put(url, data);
    return response.data;
  } catch (error) {
    console.error(`PUT ${url} failed:`, error);
    throw error;
  }
};

/**
 * Perform a PATCH request
 * @param {string} url - API endpoint
 * @param {Object} data - Request body
 * @returns {Promise<any>} Response data
 */
export const patch = async (url, data = {}) => {
  try {
    const response = await api.patch(url, data);
    return response.data;
  } catch (error) {
    console.error(`PATCH ${url} failed:`, error);
    throw error;
  }
};

/**
 * Perform a DELETE request
 * @param {string} url - API endpoint
 * @returns {Promise<any>} Response data
 */
export const del = async (url) => {
  try {
    const response = await api.delete(url);
    return response.data;
  } catch (error) {
    console.error(`DELETE ${url} failed:`, error);
    throw error;
  }
};

const apiService = {
  get,
  post,
  put,
  patch,
  delete: del,
  api // Export the axios instance for direct use if needed
};

export default apiService;

// GLOBAL SAFETY NET
// Also attach a global interceptor to the default axios instance to catch any stray axios.* calls
// and ensure they use the normalized API base and Authorization.
if (!axios.__thaklisGlobalInterceptorInstalled) {
  axios.interceptors.request.use((cfg) => {
    // Add base for relative /v1/* or /api/* calls
    ensureBaseForRelative(cfg);
    // Normalize absolute app* URLs
    if (cfg.url && /^https?:\/\//i.test(cfg.url)) {
      cfg.url = normalizeApiHost(cfg.url);
    }
    // Attach token
    try {
      const t = authService.getToken();
      if (t) {
        cfg.headers = cfg.headers || {};
        cfg.headers['Authorization'] = `Bearer ${t}`;
      }
    } catch (_) { /* ignore */ }
    // Default JSON content-type if not set
    cfg.headers = cfg.headers || {};
    if (!cfg.headers['Content-Type']) {
      cfg.headers['Content-Type'] = 'application/json';
    }
    return cfg;
  }, (err) => Promise.reject(err));
  axios.__thaklisGlobalInterceptorInstalled = true;
}
